/** biome-ignore-all lint/style/noRestrictedImports: not needed */

import { useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports -- is okay
import { useLatest } from 'react-use';

interface StorageHookOptions<T> {
  getItem: () => Promise<T | undefined>;
  setItem: (value: T) => Promise<void>;
  onSuccess?: (data: T | undefined) => void;
  enabled?: boolean;
}

export function useStorageValue<T>({
  getItem,
  setItem,
  onSuccess,
  enabled = true,
}: StorageHookOptions<T>) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(true);

  const onSuccessFn = useLatest(onSuccess);

  useEffect(() => {
    if (!enabled) return;
    let isMounted = true;
    setIsFetching(true);

    void (async () => {
      try {
        const item = await getItem();
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition  -- false positive
        if (!isMounted) return;
        setData(item);
        setError(null);
        onSuccessFn.current?.(item);
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition  -- false positive
        if (!isMounted) return;
        setError(err);
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition  -- false positive
        if (isMounted) {
          setIsLoading(false);
          setIsFetching(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [enabled, getItem, onSuccessFn]);

  const setStorageValue = useCallback(
    async (value: T) => {
      try {
        await setItem(value);
        setData(value);
      } catch (err) {
        setError(err);
      }
    },
    [setItem],
  );

  return {
    data,
    isLoading,
    isFetching,
    error,
    setStorageValue,
  };
}
